import { scry } from "@/lib/scryClient";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import PriceStrip from "@/components/PriceStrip";
import type { ScryfallCard } from "@/types/cards";
import { getCardById, getRulingsByCardId, type ScryfallRuling } from "@/lib/scryfall";
import { useNavigate } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { collection, getDocs, limit, query, where, documentId } from "firebase/firestore";
import { requestFollow } from "@/data/follow";
import ProfileInlineMenu from "@/components/profile/ProfileInlineMenu";
import { toast } from "@/components/ui/use-toast";
import { sendOffer } from "@/data/offers";

type ListingItem = {
  name: string;
  image?: string | null;
  set?: string | null;
  number?: string | null; // some callers may use collector_number; we normalize
  collector_number?: string | null;
  foil?: boolean;
  status: "sale" | "trade";
  price?: number | null;
  scryfall_id?: string | null;
  ownerUid?: string;
  username?: string;
  ownerAvatar?: string | null;
  distance?: number;
  docId?: string | null; // users/{uid}/cards/{docId}
};

type Props = {
  open: boolean;
  onClose: () => void;
  // Primary source for listing context (as used in Dashboard/Marketplace/ProfilePublic)
  item?: Partial<ListingItem> | null;
  // Legacy/compat fields (safe to omit on modern pages)
  card?: Partial<ListingItem> | null;
  title?: string;
  onMakeOffer?: () => void;
};

function isNonEmpty(s: unknown): s is string {
  return typeof s === "string" && s.trim().length > 0;
}
function scryfallBySetNum(setCode?: string | null, number?: string | null) {
  if (!isNonEmpty(setCode) || !isNonEmpty(number)) return null;
  return `https://api.scryfall.com/cards/${encodeURIComponent(setCode.toLowerCase())}/${encodeURIComponent(
    number,
  )}`;
}
async function fetchCardBySetNum(setCode?: string | null, number?: string | null, signal?: AbortSignal) {
  const url = scryfallBySetNum(setCode, number);
  if (!url) return null;
  const res = await fetch(url, { signal });
  if (!res.ok) return null;
  return (await res.json()) as ScryfallCard;
}
function imgFromScryfall(c?: ScryfallCard | null): string | null {
  if (!c) return null;
  const f = c.card_faces?.[0]?.image_uris;
  const u = c.image_uris;
  return f?.large || u?.large || f?.normal || u?.normal || f?.small || u?.small || null;
}

export default function CardDetailsModal({ open, onClose, item, card, title /* onMakeOffer (deprecated) */, onMakeOffer }: Props) {
  const navigate = useNavigate();
  const me = auth.currentUser?.uid || null;

  // MOVE src derivation ABOVE any hooks that reference it (fix TDZ runtime error)
  const src = useMemo(() => {
    const base = (item ?? card ?? {}) as Partial<ListingItem>;
    return {
      name: base.name ?? "",
      image: base.image ?? null,
      set: base.set ?? null,
      number: (base.collector_number ?? base.number) ?? null,
      foil: Boolean(base.foil),
      status: (base.status as "sale" | "trade") ?? "trade",
      price: base.price ?? null,
      scryfall_id: base.scryfall_id ?? null,
      ownerUid: base.ownerUid ?? "",
      username: base.username ?? "",
      ownerAvatar: base.ownerAvatar ?? null,
      distance: typeof base.distance === "number" ? base.distance : undefined,
      docId: base.docId ?? null,
    };
  }, [item, card]);

  // ----- unified offer state -----
  const [offerMode, setOfferMode] = useState(false);
  const [offerMessage, setOfferMessage] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [offerSubmitting, setOfferSubmitting] = useState(false);

  // reset offer form when modal closes or listing context changes
  useEffect(() => {
    if (!open) {
      setOfferMode(false);
      setOfferMessage("");
      setOfferPrice("");
      setOfferSubmitting(false);
    }
  }, [open, src.ownerUid, src.docId]);

  const canOffer =
    !!me &&
    me !== src.ownerUid &&
    (src.status === "sale" || src.status === "trade");

  async function submitUnifiedOffer() {
    if (!canOffer || !me || !src.ownerUid) return;
    if (offerSubmitting) return;
    setOfferSubmitting(true);
    try {
      await sendOffer({
        toUid: src.ownerUid,
        cardId: src.docId || src.scryfall_id || src.name,
        cardOwnerUid: src.ownerUid,
        cardName: src.name,
        scryfall_id: src.scryfall_id || undefined,
        offerType: src.status === "sale" ? "purchase" : "trade",
        price: src.status === "sale" && offerPrice ? Number(offerPrice) : null,
        message: (offerMessage || "").trim() || null,
      });
      toast({ description: "Offer sent!" });
      setTimeout(() => toast({ description: null }), 1800);
      setOfferMode(false);
      setOfferMessage("");
      setOfferPrice("");
    } catch (e) {
      console.error("[CardDetailsModal] offer failed", e);
      toast({ description: "Could not send offer." });
      setTimeout(() => toast({ description: null }), 2000);
    } finally {
      setOfferSubmitting(false);
    }
  }

  // Merge possible sources (item is the canonical one; card is legacy)
  const base = (item ?? card ?? {}) as Partial<ListingItem>;
  const sellerName = base.username || base.ownerUid || "";
  const distanceLabel =
    typeof base.distance === "number" && Number.isFinite(base.distance) ? `${base.distance.toFixed(1)} mi` : undefined;
  const priceLabel =
    base.status === "sale" ? (base.price != null ? `$${Number(base.price).toFixed(2)}` : "—") : undefined;

  // Follow state
  type FollowCaps = { status: "none" | "pending" | "approved"; privacy: "open" | "request" | "private" };
  const [uMenuOpen, setUMenuOpen] = useState(false);
  const [cap, setCap] = useState<FollowCaps | null>(null);
  function normalizePrivacy(val?: unknown): "open" | "request" | "private" {
    const v = String(val ?? "").toLowerCase();
    if (v === "open" || v === "public" || v === "everyone") return "open";
    if (v === "private") return "private";
    return "request";
  }
  useEffect(() => {
    (async () => {
      if (!open || !base.ownerUid || !me) { setCap(null); return; }
      try {
        // my follow edges (approved/pending)
        let status: FollowCaps["status"] = "none";
        try {
          const s = await getDocs(query(
            collection(db, "follows"),
            where("followerId", "==", me),
            where("status", "in", ["approved","pending"]),
            limit(200),
          ));
          s.forEach((d) => {
            const v = d.data() as any;
            if (v?.followingId === base.ownerUid) status = v.status as any;
          });
        } catch {}
        // privacy from user doc
        let privacy: FollowCaps["privacy"] = "request";
        try {
          const s = await getDocs(query(
            collection(db, "users"),
            where(documentId(), "in", [base.ownerUid]),
            limit(1),
          ));
          s.forEach((u) => {
            const v = u.data() as any;
            const raw = v?.follow_privacy ?? v?.preferences?.follow_privacy ?? null;
            privacy = normalizePrivacy(raw);
          });
        } catch {}
        setCap({ status, privacy });
      } catch { setCap(null); }
    })();
  }, [open, base.ownerUid, me]);
  function followBtn(): { visible: boolean; label: string; disabled: boolean } {
    if (!cap) return { visible: true, label: "Follow", disabled: false };
    if (cap.privacy === "private") return { visible: false, label: "Private", disabled: true };
    if (cap.status === "approved") return { visible: true, label: "Following", disabled: true };
    if (cap.status === "pending") return { visible: true, label: "Requested", disabled: true };
    return { visible: true, label: "Follow", disabled: false };
  }
  async function onFollow() {
    if (!base.ownerUid) return;
    try {
      const status = await requestFollow(base.ownerUid);
      setCap((prev) => ({ ...(prev ?? { privacy: "request" }), status }));
    } catch {}
  }

  const [sfCard, setSfCard] = useState<ScryfallCard | null>(null);
  const [rulings, setRulings] = useState<ScryfallRuling[]>([]);
  const [loading, setLoading] = useState(false);
  const acRef = useRef<AbortController | null>(null);

  // Load Scryfall details
  useEffect(() => {
    if (!open) return;
    const ac = new AbortController();
    acRef.current?.abort();
    acRef.current = ac;
    const scryId = base.scryfall_id || null;

    async function run() {
      setLoading(true);
      try {
        let c: ScryfallCard | null = null;
        if (isNonEmpty(scryId)) {
          c = await getCardById(scryId!, ac.signal);
        } else if (isNonEmpty(base.set) && isNonEmpty(base.number)) {
          c = await fetchCardBySetNum(base.set!, base.number!, ac.signal);
        }
        setSfCard(c);
        // rulings only by Scryfall id
        const rul = isNonEmpty(c?.id) ? await getRulingsByCardId(c!.id, ac.signal).catch(() => []) : [];
        setRulings(Array.isArray(rul) ? rul : []);
      } catch {
        // ignore; show listing info at least
        setSfCard((prev) => prev ?? null);
        setRulings([]);
      } finally {
        if (acRef.current === ac) setLoading(false);
      }
    }
  run();
    return () => ac.abort();
  }, [open, base.scryfall_id, base.set, base.number]);

  if (!open) return null;

  const heading = title || sfCard?.name || base.name || "Card details";
  const setLine = `${(sfCard?.set || base.set || "").toString().toUpperCase()}${
    sfCard?.collector_number || base.number ? ` • #${sfCard?.collector_number ?? base.number}` : ""
  }${base.foil ? " • Foil" : ""}`;

  const imageUrl =
    base.image ||
    imgFromScryfall(sfCard) ||
    (isNonEmpty(base.scryfall_id) ? ` + scryImageUrl(base.scryfall_id!, "large") + ` : "/images/cardback.png");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center md:p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70" aria-hidden onClick={onClose} />
      <div
        className="relative z-[101] w-full md:w-[min(95vw,1000px)] h-[100dvh] md:h-auto max-h-[100dvh] md:max-h-[92vh]
                   overflow-hidden md:rounded-2xl rounded-none border border-slate-700 bg-gray-950 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-gray-950/90 backdrop-blur supports-[backdrop-filter]:bg-gray-950/60">
          <div className="text-base md:text-lg font-semibold text-white truncate">{heading}</div>
          <button
            type="button"
            className="px-3 py-1 rounded-md border border-white/10 hover:bg-white/5 text-white text-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left: Image */}
            <div className="bg-black p-0 md:p-3">
              <div className="w-full aspect-[3/4] bg-black overflow-hidden md:rounded-lg">
                <img src={imageUrl} alt={heading} className="w-full h-full object-contain" />
              </div>
            </div>

            {/* Right: Oracle + Listing details */}
            <div className="p-4 space-y-4">
              {/* Oracle summary */}
              <div className="text-sm text-slate-300">{setLine}</div>

              {/* Oracle text */}
              {(sfCard?.oracle_text || sfCard?.type_line) && (
                <div className="space-y-2">
                  {sfCard?.type_line && <div className="text-[13px] text-slate-400">{sfCard.type_line}</div>}
                  {sfCard?.oracle_text && (
                    <div className="whitespace-pre-line text-sm text-slate-200">{sfCard.oracle_text}</div>
                  )}
                </div>
              )}

              {/* Prices (Scryfall) */}
              {isNonEmpty(sfCard?.id) && (
                <div className="pt-1">
                  <PriceStrip scryfallId={sfCard!.id} compact />
                </div>
              )}

              {/* Rulings */}
              {Array.isArray(rulings) && rulings.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-slate-300">Rulings</div>
                  <div className="space-y-1 max-h-40 overflow-auto pr-1">
                    {rulings.map((r) => (
                      <div key={r.id} className="text-[12px] text-slate-300 leading-snug">
                        <span className="opacity-70">{r.published_at}: </span>
                        {r.comment}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Listing details */}
              <div className="mt-2 rounded-lg border border-slate-800 bg-black/30 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    className="shrink-0 rounded-full border border-slate-700 overflow-hidden"
                    onClick={() =>
                      base.username
                        ? navigate(`/${base.username}`)
                        : base.ownerUid
                        ? navigate(`/inbox?to=${encodeURIComponent(base.ownerUid)}`)
                        : void 0
                    }
                    aria-label={`@${sellerName}`}
                    title={`@${sellerName}`}
                  >
                    <img
                      src={base.ownerAvatar || "/images/avatars/default.png"}
                      className="h-8 w-8 object-cover"
                      alt={sellerName}
                    />
                  </button>
                  <div className="min-w-0">
                    <button
                      className="text-sm text-white underline truncate"
                      onClick={() =>
                        base.username
                          ? navigate(`/${base.username}`)
                          : void 0
                      }
                      title={`@${sellerName || "seller"}`}
                    >
                      @{sellerName || "seller"}
                    </button>
                    <div className="text-xs text-slate-400">
                      {distanceLabel ? `${distanceLabel} • ` : ""}
                      {base.status === "sale" ? "For Sale" : "For Trade"}
                      {base.status === "sale" ? ` • ${priceLabel}` : ""}
                      {base.foil ? " • Foil" : ""}
                    </div>
                  </div>
                </div>

                {/* Listing meta */}
                <div className="text-xs text-slate-400">
                  {isNonEmpty(base.set) && <span className="mr-2">Set: {String(base.set).toUpperCase()}</span>}
                  {isNonEmpty(base.number) && <span className="mr-2">No: {base.number}</span>}
                  {isNonEmpty(base.docId) && <span className="opacity-60">Doc: {base.docId}</span>}
                </div>

                {/* Offer form (inline) */}
                {canOffer && offerMode && (
                  <div className="mt-3 pt-3 border-t border-slate-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-white">
                        {base.status === "sale" ? "Make an Offer" : "Propose a Trade"}
                      </h4>
                      <button
                        onClick={() => !offerSubmitting && setOfferMode(false)}
                        className="text-xs text-slate-400 hover:text-slate-200"
                        disabled={offerSubmitting}
                      >
                        Cancel
                      </button>
                    </div>

                    {base.status === "sale" && (
                      <div className="grid gap-1">
                        <label htmlFor="unified-offer-price" className="text-xs text-slate-300">
                          Offer (USD, optional)
                        </label>
                        <input
                          id="unified-offer-price"
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          className="border border-slate-700 rounded px-2 py-1 text-sm text-black"
                          placeholder={
                            base.price != null ? `e.g. ${Number(base.price).toFixed(2)}` : "Enter amount"
                          }
                          value={offerPrice}
                          onChange={(e) => setOfferPrice(e.target.value)}
                          disabled={offerSubmitting}
                        />
                      </div>
                    )}

                    <div className="grid gap-1">
                      <label htmlFor="unified-offer-message" className="text-xs text-slate-300">
                        Message
                      </label>
                      <textarea
                        id="unified-offer-message"
                        rows={4}
                        className="border border-slate-700 rounded px-2 py-1 text-sm text-black"
                        placeholder={
                          base.status === "sale"
                            ? "Hi! I'm interested in buying this card…"
                            : "Hi! I'm interested in trading for this card. I can offer…"
                        }
                        value={offerMessage}
                        onChange={(e) => setOfferMessage(e.target.value)}
                        disabled={offerSubmitting}
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button
                        onClick={() => void submitUnifiedOffer()}
                        disabled={offerSubmitting}
                      >
                        {offerSubmitting
                          ? "Sending…"
                          : base.status === "sale"
                          ? "Send Offer"
                          : "Send Trade Proposal"}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      A notification will be sent to the owner.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Floating Offer button (now just toggles form) */}
        {canOffer && !offerMode && (
          <div className="pointer-events-none absolute right-4 bottom-4 md:right-6 md:bottom-6">
            <Button
              className="pointer-events-auto shadow-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              onClick={() => {
                // Prefer internal unified form; fall back to legacy onMakeOffer only if explicitly supplied
                if (onMakeOffer) {
                  // If you want to keep legacy behavior, uncomment next line and remove unified path.
                  // return onMakeOffer();
                  setOfferMode(true);
                } else {
                  setOfferMode(true);
                }
              }}
            >
              Make offer
            </Button>
          </div>
        )}

        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}

export async function scryfallCardUrl(id: string) {
  // If used for fetching JSON, prefer backend
  return (await scry.getById(id)).url ?? `/api/scry/cards/${encodeURIComponent(id)}`;
}


